import React from 'react';
import Typed from 'react-typed';
import PropTypes from 'prop-types';
import { alpha, useTheme } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Chip from '@material-ui/core/Chip';
import { Link as RouterLink } from 'react-router-dom';
import WhatsAppIcon from '@material-ui/icons/WhatsApp';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import LiveTvIcon from '@material-ui/icons/LiveTv';
import MovieCreationIcon from '@material-ui/icons/MovieCreation';
import MenuBookIcon from '@material-ui/icons/MenuBook';
import ShoppingCartIcon from '@material-ui/icons/ShoppingCart';
import RocketIllustration from 'svg/illustrations/Rocket';
import handleOpenWhatsApp from 'services/whatsappService';

const DEFAULT_TITLE = '¿Estás listo para dejar atrás lo convencional?';
const DEFAULT_DESCRIPTION =
  'Alcanza a tu audiencia, escala tu impacto y haz que tu marca resuene en cada rincón del mundo digital.';

const TYPED_STRINGS = ['innovación multimedia.', 'conexión real.', 'comunidad global.', 'comunicación 360°.'];

const HIGHLIGHTS = [
  { label: 'Live & Streaming global', Icon: LiveTvIcon, accent: '#1565c0' },
  { label: 'TNO Media Lab', Icon: MovieCreationIcon, accent: '#6a1b9a' },
  { label: 'TNO Digest', Icon: MenuBookIcon, accent: '#00838f' },
  { label: 'Paquetes comerciales', Icon: ShoppingCartIcon, accent: '#c2185b' },
];

const AreYouReady = ({ content = {} }) => {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const secondary = theme.palette.secondary?.main || '#00838f';
  const title = content.title || DEFAULT_TITLE;
  const description = content.description || DEFAULT_DESCRIPTION;

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 4,
        my: { xs: 2, md: 4 },
        px: { xs: 2, md: 4 },
        py: { xs: 3.5, md: 5 },
        background: `linear-gradient(155deg, ${alpha(primary, 0.1)} 0%, ${alpha(secondary, 0.14)} 42%, ${alpha(
          primary,
          0.06,
        )} 100%)`,
        border: `1px solid ${alpha(primary, 0.18)}`,
        boxShadow: `0 24px 56px ${alpha(primary, 0.12)}`,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          width: 320,
          height: 320,
          borderRadius: '50%',
          top: -120,
          right: -80,
          background: `radial-gradient(circle, ${alpha(secondary, 0.22)} 0%, transparent 70%)`,
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          width: 240,
          height: 240,
          borderRadius: '50%',
          bottom: -60,
          left: -40,
          background: `radial-gradient(circle, ${alpha(primary, 0.15)} 0%, transparent 70%)`,
          pointerEvents: 'none',
        }}
      />

      <Grid container spacing={4} alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid item xs={12} md={7}>
          <Typography
            sx={{ textTransform: 'uppercase', fontWeight: 700, letterSpacing: 2.5, fontSize: '0.78rem' }}
            color="secondary"
            gutterBottom
          >
            {content.overline || 'Siguiente paso'}
          </Typography>
          <Typography
            component="h2"
            sx={{
              fontWeight: 900,
              fontSize: { xs: '1.85rem', md: '2.5rem' },
              letterSpacing: '-0.02em',
              color: 'primary.main',
              lineHeight: 1.15,
              mb: 2,
            }}
          >
            {title}
          </Typography>

          <Typography
            sx={{
              fontWeight: 800,
              fontSize: { xs: '1.1rem', md: '1.35rem' },
              lineHeight: 1.45,
              mb: 2,
              color: 'text.primary',
            }}
          >
            Únete a TNO Network y desata el poder de la{' '}
            <Typography component="span" sx={{ color: 'primary.main', fontWeight: 900, fontSize: 'inherit' }}>
              <Typed strings={TYPED_STRINGS} typeSpeed={80} backSpeed={40} loop />
            </Typography>
          </Typography>

          <Typography sx={{ color: 'text.secondary', fontSize: '1.02rem', lineHeight: 1.7, mb: 2.5, maxWidth: 560 }}>
            {description}
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
            {HIGHLIGHTS.map(({ label, Icon, accent }) => (
              <Chip
                key={label}
                icon={<Icon sx={{ fontSize: 18, color: `${accent} !important` }} />}
                label={label}
                size="small"
                sx={{
                  fontWeight: 700,
                  fontSize: '0.78rem',
                  bgcolor: alpha(accent, 0.1),
                  border: `1px solid ${alpha(accent, 0.22)}`,
                  color: 'text.primary',
                }}
              />
            ))}
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, maxWidth: '100%' }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={<WhatsAppIcon />}
              onClick={handleOpenWhatsApp}
              sx={{
                borderRadius: 999,
                px: 3,
                fontWeight: 700,
                width: { xs: '100%', sm: 'auto' },
                boxShadow: `0 8px 24px ${alpha(primary, 0.35)}`,
              }}
            >
              Agenda una sesión en TNO Media Lab
            </Button>
            <Button
              component={RouterLink}
              to="/paquetes"
              variant="outlined"
              color="primary"
              size="large"
              endIcon={<ArrowForwardIcon />}
              sx={{
                borderRadius: 999,
                px: 2.75,
                fontWeight: 700,
                borderWidth: 2,
                bgcolor: 'background.paper',
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              Ver paquetes
            </Button>
            <Button
              component="a"
              href="https://www.tnonetwork.com"
              target="_blank"
              rel="noopener noreferrer"
              variant="text"
              color="primary"
              size="large"
              endIcon={<OpenInNewIcon />}
              sx={{ borderRadius: 999, fontWeight: 700, width: { xs: '100%', sm: 'auto' } }}
            >
              tnonetwork.com
            </Button>
          </Box>
        </Grid>

        <Grid item xs={12} md={5}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: { xs: 2, md: 3 },
              borderRadius: 4,
              bgcolor: 'background.paper',
              border: `1px solid ${alpha(primary, 0.12)}`,
              boxShadow: `0 16px 40px ${alpha(primary, 0.1)}`,
              minHeight: { xs: 220, md: 300 },
            }}
          >
            <Box sx={{ width: '100%', maxWidth: 360 }}>
              <RocketIllustration width="100%" height="auto" />
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

AreYouReady.propTypes = {
  content: PropTypes.shape({
    overline: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
  }),
};

AreYouReady.defaultProps = {
  content: {},
};

export default AreYouReady;
